import React, { useState } from "react";
import { useAuth } from "@/store/AuthContext";
import { Eye, EyeOff, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!username.trim() || !password) { setError("Please enter your username and password."); return; }
    setBusy(true);
    try {
      await login(username.trim(), password);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "Incorrect username or password.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center px-4" style={{ backgroundColor: "#0F2043" }} data-testid="login-screen">
      {/* Demo pill */}
      <div className="fixed top-4 right-4 rounded-full bg-cnp-amber/90 text-[#3A2A00] text-[11px] font-bold uppercase tracking-wider px-3 py-1 shadow" data-testid="demo-pill">
        Demo Environment
      </div>

      {/* Logo tile */}
      <div className="bg-white rounded-2xl px-6 py-4 shadow-lg mb-5">
        <img src="/centerpoint-logo.png" alt="CenterPoint Energy" className="h-12 w-auto object-contain" />
      </div>
      <h1 className="text-white font-extrabold tracking-[0.12em] mb-1" style={{ fontSize: "28px" }} data-testid="login-wordmark">E.A.R.S</h1>
      <p className="text-white/50 text-sm mb-3">CenterPoint Energy — Enterprise Application Readiness Status</p>
      <span data-testid="login-sample-data" className="inline-flex items-center gap-1.5 rounded-full bg-cnp-amber text-[#3A2A00] text-[11px] font-extrabold uppercase tracking-[0.15em] px-3 py-1 mb-5">
        <Eye size={13} /> Sample Data
      </span>

      {/* Card */}
      <form onSubmit={submit} className="bg-white rounded-xl shadow-xl w-full max-w-[380px] p-8" data-testid="login-card">
        <h2 className="text-lg font-bold text-cnp-navy mb-5">Sign in</h2>

        <label className="block text-xs font-semibold text-cnp-textMuted mb-1" htmlFor="username">Username</label>
        <input id="username" data-testid="login-username" autoFocus autoComplete="username"
          value={username} onChange={(e) => { setUsername(e.target.value); setError(""); }}
          className="w-full rounded-lg border border-cnp-border px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-cnp-blue/40" />

        <label className="block text-xs font-semibold text-cnp-textMuted mb-1" htmlFor="password">Password</label>
        <div className="relative mb-4">
          <input id="password" data-testid="login-password" type={show ? "text" : "password"} autoComplete="current-password"
            value={password} onChange={(e) => { setPassword(e.target.value); setError(""); }}
            className="w-full rounded-lg border border-cnp-border px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-cnp-blue/40" />
          <button type="button" onClick={() => setShow((s) => !s)} data-testid="login-toggle-password"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-cnp-textMuted hover:text-cnp-text" tabIndex={-1}>
            {show ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>

        {error && <div className="text-[13px] text-cnp-red mb-3" data-testid="login-error">{error}</div>}

        <button type="submit" disabled={busy} data-testid="login-submit"
          className="w-full rounded-lg bg-cnp-blue text-white font-semibold py-2.5 text-sm hover:bg-cnp-blue/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
          {busy && <Loader2 size={15} className="animate-spin" />} Sign in
        </button>
      </form>

      <p className="text-white/40 text-xs mt-5">Authorised access only. Activity is logged.</p>
    </div>
  );
}
